import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ApiError, paraApiError, type ApiRequest } from '../api/call-api';

export { ApiError } from '../api/call-api';

type QueryState<TResponse> =
  | { status: 'idle'; data: undefined; error: null }
  | { status: 'loading'; data: TResponse | undefined; error: null }
  | { status: 'success'; data: TResponse; error: null }
  | { status: 'error'; data: undefined; error: ApiError };

type QueryOptions = {
  enabled?: boolean;
  keepPreviousData?: boolean;
};

export function useApiQuery<TArgs, TResponse>(
  request: ApiRequest<TArgs, TResponse>,
  args: TArgs,
  options: QueryOptions = {},
) {
  const { enabled = true, keepPreviousData = false } = options;
  const [state, setState] = useState<QueryState<TResponse>>({
    status: 'loading',
    data: undefined,
    error: null,
  });
  const requestId = useRef(0);
  const argsKey = JSON.stringify(args);

  const refetch = useCallback(async () => {
    if (!enabled) {
      return;
    }
    const currentRequestId = ++requestId.current;
    setState((atual) => ({
      status: 'loading',
      data: keepPreviousData ? atual.data : undefined,
      error: null,
    }));
    try {
      const data = await request(args);
      if (currentRequestId === requestId.current) {
        setState({ status: 'success', data, error: null });
      }
    } catch (error) {
      if (currentRequestId === requestId.current) {
        setState({ status: 'error', data: undefined, error: paraApiError(error) });
      }
    }
  }, [request, argsKey, enabled, keepPreviousData]);

  useEffect(() => {
    void refetch();
    return () => {
      requestId.current += 1;
    };
  }, [refetch]);

  return {
    data: state.data,
    error: state.error,
    loading: state.status === 'loading',
    refetch: () => refetch(),
  };
}

type MutationFunction<TArgs, TResponse> = [TArgs] extends [void]
  ? () => Promise<TResponse>
  : (args: TArgs) => Promise<TResponse>;

export function useApiMutation<TArgs, TResponse>(
  request: ApiRequest<TArgs, TResponse>,
  options: { silent?: boolean } = {},
) {
  const { silent = false } = options;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const mutate = useCallback(
    async (args: TArgs): Promise<TResponse> => {
      setLoading(true);
      setError(null);
      try {
        return await request(args);
      } catch (err) {
        const normalized = paraApiError(err);
        setError(normalized);
        if (!silent) {
          toast.error(normalized.message);
        }
        throw normalized;
      } finally {
        setLoading(false);
      }
    },
    [request, silent],
  );

  return { mutate: mutate as MutationFunction<TArgs, TResponse>, loading, error };
}

type ExecuteFunction<TArgs, TResponse> = [TArgs] extends [void]
  ? () => Promise<TResponse>
  : (args: TArgs) => Promise<TResponse>;

export function useApiLazyQuery<TArgs, TResponse>(request: ApiRequest<TArgs, TResponse>) {
  const [state, setState] = useState<QueryState<TResponse>>({
    status: 'idle',
    data: undefined,
    error: null,
  });

  const execute = useCallback(
    async (args: TArgs): Promise<TResponse> => {
      setState({ status: 'loading', data: undefined, error: null });
      try {
        const data = await request(args);
        setState({ status: 'success', data, error: null });
        return data;
      } catch (err) {
        const normalized = paraApiError(err);
        setState({ status: 'error', data: undefined, error: normalized });
        throw normalized;
      }
    },
    [request],
  );

  return {
    data: state.data,
    error: state.error,
    loading: state.status === 'loading',
    execute: execute as ExecuteFunction<TArgs, TResponse>,
  };
}
